import { useCallback, useMemo, useState } from 'react';
import { DSIcon, DSTypography } from '@eiv2/design-system';
import { PageHeader } from '../components/PageHeader';
import { SectionCard } from '../components/SectionCard';
import { RequestState } from '../components/RequestState';
import { CollapsibleFiltersCard } from '../components/CollapsibleFiltersCard';
import { DateRangePicker } from '../components/DateRangePicker';
import { SourceFilter } from '../components/SourceFilter';
import { IconTooltipButton } from '../components/IconTooltipButton';
import { adminApi } from '../api/client';
import { useApiResource } from '../hooks/useApiResource';
import { formatNumber, formatPercent } from '../services/format';
import { buildFunnelView } from '../services/funnelView';
import { getDefaultWindow, type DateRange } from '../services/dateWindow';

/** Tons distintos a partir dos tokens do DS (brand + feedback). */
const FUNNEL_BAR_TONES = [
  'bg-brand-primary-3',
  'bg-brand-primary-3/75',
  'bg-[#8E5CE3]',
  'bg-[#A078EB]',
  'bg-[#C4B5FD]',
  'bg-[#D3C0FF]'
] as const;

function barToneClass(event: string, index: number): string {
  if (event === 'payment_approved') return 'bg-feedback-success';
  if (event === 'checkout_abandoned' || event === 'payment_failed') {
    return 'bg-feedback-danger';
  }
  return FUNNEL_BAR_TONES[index % FUNNEL_BAR_TONES.length] ?? 'bg-brand-primary-3';
}

export function FunnelPage({ api = adminApi }: { api?: Pick<typeof adminApi, 'getFunnel'> } = {}) {
  const [window, setWindow] = useState<DateRange>(() => getDefaultWindow());
  const [source, setSource] = useState('');

  const loader = useCallback(
    (signal: AbortSignal) =>
      api.getFunnel({ from: window.from, to: window.to, source: source || undefined }, signal),
    [api, window.from, window.to, source]
  );
  const funnel = useApiResource(loader, [api, window.from, window.to, source]);
  const view = useMemo(() => buildFunnelView(funnel.data?.steps ?? []), [funnel.data]);

  return (
    <div>
      <PageHeader title="Funil" subtitle="Jornadas registradas por etapa no período." />

      <CollapsibleFiltersCard
        className="mb-6"
        storageKey="funnel"
        title="Filtrar por período e origem"
      >
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start">
          <DateRangePicker className="lg:max-w-[520px]" value={window} onChange={setWindow} />
          <SourceFilter compact value={source} onChange={setSource} />
        </div>
      </CollapsibleFiltersCard>

      {funnel.error ? (
        <RequestState
          title="Não foi possível carregar o funil"
          error={funnel.error}
          onRetry={funnel.reload}
        />
      ) : funnel.isLoading && !funnel.data ? (
        <RequestState title="Carregando funil" message="Buscando distribuição por etapa." />
      ) : (
        <SectionCard
          action={
            <IconTooltipButton
              disabled={funnel.isLoading}
              label="Atualizar"
              onClick={funnel.reload}
            >
              <DSIcon name="Refresh" size={20} />
            </IconTooltipButton>
          }
          subtitle="Contagens independentes no período e origem filtrados."
          title="Jornadas por etapa"
        >
          {!view.hasData ? (
            <div className="py-6">
              <DSTypography as="p" className="text-[16px] font-semibold text-dark-3">
                Sem registros nas etapas do funil
              </DSTypography>
              <DSTypography as="p" className="mt-1 text-[13px] font-normal text-gray-5">
                Nenhum evento dessas etapas no período e na origem selecionados.
              </DSTypography>
            </div>
          ) : (
            <>
              {!view.hasBase ? (
                <div className="mb-4 rounded-[12px] bg-brand-primary-3-20 px-4 py-3">
                  <DSTypography as="p" className="text-[13px] font-normal text-dark-3">
                    <strong className="font-semibold">Sem base de cálculo.</strong> Nenhuma abertura
                    de checkout neste período e origem — as taxas por etapa ficam indisponíveis.
                  </DSTypography>
                </div>
              ) : null}

              <div
                aria-hidden
                className="mb-2 hidden grid-cols-[minmax(180px,220px)_minmax(0,1fr)_96px_160px] gap-x-6 text-gray-5 md:grid"
              >
                <DSTypography as="span" className="text-[12px] font-semibold">
                  Etapa
                </DSTypography>
                <DSTypography as="span" className="text-[12px] font-semibold">
                  Volume
                </DSTypography>
                <DSTypography as="span" className="text-right text-[12px] font-semibold">
                  Qtd.
                </DSTypography>
                <DSTypography as="span" className="text-right text-[12px] font-semibold">
                  % das aberturas
                </DSTypography>
              </div>

              <ol className="m-0 list-none p-0">
                {view.rows.map((step, index) => (
                  <li
                    key={step.event}
                    className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-4 gap-y-3 border-t border-gray-3 py-4 md:grid-cols-[minmax(180px,220px)_minmax(0,1fr)_96px_160px] md:gap-x-6"
                    data-stage={step.event}
                  >
                    <div className="col-start-1 row-start-1 min-w-0">
                      <DSTypography as="p" className="text-[14px] font-semibold text-dark-3">
                        {step.label}
                      </DSTypography>
                    </div>

                    <div
                      aria-hidden
                      className="col-span-full row-start-2 h-3 overflow-hidden rounded-full bg-gray-2 md:col-span-1 md:col-start-2 md:row-start-1"
                    >
                      <div
                        className={`h-full rounded-full transition-[width] duration-300 ease-out ${barToneClass(step.event, index)}`}
                        data-stage-bar={step.event}
                        style={{ width: `${step.widthPercent}%` }}
                      />
                    </div>

                    <DSTypography
                      aria-label={`${formatNumber(step.count)} jornadas`}
                      as="p"
                      className="col-start-2 row-start-1 text-right text-[14px] font-bold tabular-nums text-dark-3 md:col-start-3"
                    >
                      {formatNumber(step.count)}
                    </DSTypography>

                    <DSTypography
                      aria-label={
                        step.rate === null
                          ? 'Sem base de cálculo'
                          : `${formatPercent(step.rate)} das aberturas`
                      }
                      as="p"
                      className="col-span-full row-start-3 text-[13px] font-normal tabular-nums text-gray-5 md:col-span-1 md:col-start-4 md:row-start-1 md:text-right"
                    >
                      {step.rate === null ? (
                        'Sem base de cálculo'
                      ) : (
                        <>
                          {formatPercent(step.rate)}
                          <span className="md:hidden"> das aberturas</span>
                        </>
                      )}
                    </DSTypography>
                  </li>
                ))}
              </ol>
            </>
          )}
        </SectionCard>
      )}
    </div>
  );
}
